import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { useGetKadryInterestedQuery, useMarkKadryContactedMutation } from '@shared/api';
import {
  KADRY_REQUEST_STATUS_LABELS,
  KADRY_REQUEST_STATUS_TONES,
  kadryDateTime,
  kadryPlural,
  useKadryAccess,
  type KadryInterestedRequest,
  type KadryRegistryRow,
} from '@entities/kadry';
import {
  KadryCandidateFinder,
  KadryCardHead,
  KadryEmpty,
  KadryErrorBanner,
  KadryMuted,
  KadryOutreachChat,
  KadryPageHeader,
  KadryStack,
  kadryCandidateMatches,
  kadrySortCandidates,
  useKadryCandidateSort,
} from './ui';

const SiteTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const SiteBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Vacancies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $alert: boolean }>`
  display: grid;
  grid-template-columns: 44px minmax(0, 1.4fr) minmax(0, 1fr) minmax(160px, auto);
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $alert }) => ($alert ? theme.colors.surface : 'transparent')};

  @media (max-width: 720px) {
    grid-template-columns: 44px minmax(0, 1fr);
  }
`;

const ChatCell = styled.div`
  grid-column: 2 / -1;

  @media (max-width: 720px) {
    grid-column: 1 / -1;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Score = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-align: center;
`;

const Name = styled.div`
  font-weight: 600;
`;

const Small = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CHANNEL: Record<string, string> = {
  web: 'по ссылке',
  telegram: 'в Telegram',
  max: 'в MAX',
  platform: 'сам откликнулся на вакансию',
};
const SOURCE: Record<string, string> = { hh: 'hh.ru', rabota: 'Работа.ру' };

const NO_SITE = 'Без объекта';

/** Сортировки страницы: внутри каждой вакансии. */
const SORTS = ['fresh', 'waiting', 'score', 'experience', 'name'] as const;

/** Срез страницы (`?show=`): переходы с «Обзора» ведут сразу в нужный. */
type Show = 'all' | 'waiting' | 'not_contacted';
const SHOW_LABELS: Record<Show, string> = {
  all: 'Все',
  waiting: 'Ждут звонка',
  not_contacted: 'Пишут: не связались',
};

/** Те же правила, что у счётчиков «Обзора». */
function inShow(row: KadryRegistryRow, show: Show): boolean {
  const o = row.outreach;
  if (!o) return false;
  if (show === 'waiting') return !o.hr_contacted_at && o.followup_answered_at == null;
  if (show === 'not_contacted') return o.hr_not_contacted;
  return true;
}

function parseShow(v: string | null): Show {
  return v === 'waiting' || v === 'not_contacted' ? v : 'all';
}

/** Группы: объект → вакансии (заявки). Объекты со свежими ответами — выше. */
function bySite(groups: KadryInterestedRequest[]): { site: string; requests: KadryInterestedRequest[] }[] {
  const out: { site: string; requests: KadryInterestedRequest[] }[] = [];
  for (const g of groups) {
    const site = g.work_site_name ?? NO_SITE;
    const cur = out.find((x) => x.site === site);
    if (cur) cur.requests.push(g);
    else out.push({ site, requests: [g] });
  }
  return out;
}

function Candidate({ row, requestId, canMark }: { row: KadryRegistryRow; requestId: string; canMark: boolean }) {
  const [mark, markState] = useMarkKadryContactedMutation();
  const o = row.outreach;
  if (!o) return null;
  return (
    <Row $alert={o.hr_not_contacted}>
      <Score title="Балл соответствия профилю (0–100)">{Math.round(row.match.score)}</Score>
      <div>
        <Name>{row.full_name ?? 'ФИО появится после открытия контактов'}</Name>
        <Small>{row.headline ?? 'Резюме без названия должности'}</Small>
        {row.experience_years != null && <Small>опыт в роли {Math.round(row.experience_years)} г.</Small>}
      </div>
      <div>
        {row.phones.map((p) => (
          <div key={p}>
            <a href={`tel:+${p.replace(/\D/g, '')}`}>+{p.replace(/\D/g, '')}</a>
          </div>
        ))}
        {row.emails.map((e) => (
          <Small key={e}>
            <a href={`mailto:${e}`}>{e}</a>
          </Small>
        ))}
        {row.resume_url && (
          <Small>
            <a href={row.resume_url} target="_blank" rel="noreferrer">
              Резюме на hh.ru
            </a>
          </Small>
        )}
      </div>
      <Side>
        <Badge $tone="success">Подходит</Badge>
        {o.auto && <Small>предложение отправлено автоматически</Small>}
        {o.responded_at && (
          <Small>
            {kadryDateTime(o.responded_at)}
            {o.response_channel ? `, ${CHANNEL[o.response_channel] ?? o.response_channel}` : ''}
          </Small>
        )}
        {o.hr_contacted_at ? (
          <Badge $tone="success">Связались {kadryDateTime(o.hr_contacted_at)}</Badge>
        ) : o.hr_not_contacted ? (
          <Badge $tone="danger">Кандидат пишет: не связались</Badge>
        ) : (
          <Badge $tone="warning">Ждёт звонка</Badge>
        )}
        {canMark && !o.hr_contacted_at && (
          <Button
            type="button"
            $size="sm"
            $variant="secondary"
            disabled={markState.isLoading}
            onClick={() => mark({ outreachId: o.id, requestId })}
          >
            Связались
          </Button>
        )}
      </Side>
      <ChatCell>
        <KadryOutreachChat outreach={o} comments={row.comments} requestId={requestId} canWrite={canMark} />
      </ChatCell>
    </Row>
  );
}

function VacancyCard({ g, canMark }: { g: KadryInterestedRequest; canMark: boolean }) {
  const waiting = g.rows.filter((r) => r.outreach && !r.outreach.hr_contacted_at).length;
  return (
    <Card>
      <KadryCardHead>
        <CardTitle>
          <Link to={`/agents/kadry/requests/${g.request_id}`}>{g.title}</Link>
        </CardTitle>
        <Badge $tone={KADRY_REQUEST_STATUS_TONES[g.status]}>{KADRY_REQUEST_STATUS_LABELS[g.status]}</Badge>
      </KadryCardHead>
      <Vacancies>
        <span>
          {g.rows.length} {kadryPlural(g.rows.length, ['кандидат', 'кандидата', 'кандидатов'])}, ждут звонка{' '}
          {waiting}
        </span>
        {g.vacancies.map((v) => (
          <a key={v.source} href={v.url} target="_blank" rel="noreferrer">
            Вакансия на {SOURCE[v.source] ?? v.source}
          </a>
        ))}
      </Vacancies>
      <Rows>
        {g.rows.map((r) => (
          <Candidate key={r.match.id} row={r} requestId={g.request_id} canMark={canMark} />
        ))}
      </Rows>
    </Card>
  );
}

/** «Подходит»: все кандидаты, ответившие «Подходит», по объектам и вакансиям. */
export function KadryInterested() {
  const { data, isLoading, error, refetch } = useGetKadryInterestedQuery(undefined, {
    pollingInterval: 60000,
  });
  const { canWrite } = useKadryAccess();
  const canMark = canWrite('match_status_change');
  const [params, setParams] = useSearchParams();
  const show = parseShow(params.get('show'));
  const counts = useMemo(() => {
    const rows = (data ?? []).flatMap((g) => g.rows);
    return {
      all: rows.length,
      waiting: rows.filter((r) => inShow(r, 'waiting')).length,
      not_contacted: rows.filter((r) => inShow(r, 'not_contacted')).length,
    } satisfies Record<Show, number>;
  }, [data]);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useKadryCandidateSort('interested', SORTS, 'fresh');
  const inSlice = useMemo(
    () => (data ?? []).reduce((n, g) => n + g.rows.filter((r) => inShow(r, show)).length, 0),
    [data, show],
  );
  const shown = useMemo(
    () =>
      (data ?? [])
        .map((g) => {
          // Название вакансии и объект тоже ищутся: «сухой лог», «машинист».
          const extra = [g.title, g.work_site_name ?? ''];
          const rows = g.rows.filter((r) => inShow(r, show) && kadryCandidateMatches(r, query, extra));
          return { ...g, rows: kadrySortCandidates(rows, sort) };
        })
        .filter((g) => g.rows.length > 0),
    [data, show, query, sort],
  );
  const found = shown.reduce((n, g) => n + g.rows.length, 0);
  const searching = query.trim().length > 0;
  const sites = useMemo(() => bySite(shown), [shown]);
  const allSites = useMemo(() => bySite(data ?? []).length, [data]);
  const total = counts.all;

  function pick(next: Show) {
    setParams(next === 'all' ? {} : { show: next }, { replace: true });
  }

  return (
    <>
      <KadryPageHeader
        title="Подходит"
        subtitle="Кандидаты, которые ответили «Подходит», — по объектам и вакансиям. Позвоните и отметьте «Связались»: через 5 дней кандидата спросят, связались ли с ним."
      />
      {isLoading ? (
        <Card>
          <SkeletonLine $width="70%" />
          <SkeletonLine $width="55%" />
        </Card>
      ) : error ? (
        <KadryErrorBanner error={error} onRefresh={refetch} />
      ) : total === 0 ? (
        <Card>
          <KadryEmpty>Пока никто не ответил «Подходит». Ответы появятся здесь сразу.</KadryEmpty>
        </Card>
      ) : (
        <KadryStack>
          <Filters role="group" aria-label="Показать">
            {(Object.keys(SHOW_LABELS) as Show[]).map((k) => (
              <Button
                key={k}
                type="button"
                $size="sm"
                $variant={show === k ? 'primary' : 'secondary'}
                aria-pressed={show === k}
                onClick={() => pick(k)}
              >
                {SHOW_LABELS[k]} · {counts[k]}
              </Button>
            ))}
          </Filters>
          <KadryMuted>
            {total} {kadryPlural(total, ['кандидат', 'кандидата', 'кандидатов'])} в {data?.length}{' '}
            {kadryPlural(data?.length ?? 0, ['вакансии', 'вакансиях', 'вакансиях'])} на {allSites}{' '}
            {kadryPlural(allSites, ['объекте', 'объектах', 'объектах'])}
            {show !== 'all' ? ` · показано: ${counts[show]}` : ''}
          </KadryMuted>
          <KadryCandidateFinder
            query={query}
            onQuery={setQuery}
            sort={sort}
            onSort={setSort}
            options={SORTS}
            found={found}
            total={inSlice}
            placeholder="Поиск: ФИО, телефон, должность, вакансия, объект, комментарий"
          />
          {sites.length === 0 && (
            <Card>
              <KadryEmpty>
                {searching
                  ? 'Никого не нашли — проверьте написание или переключите фильтр выше.'
                  : show === 'waiting'
                    ? 'Все, кто ответил «Подходит», уже отмечены.'
                    : 'Жалоб «не связались» нет.'}
              </KadryEmpty>
            </Card>
          )}
          {sites.map((s) => (
            <SiteBlock key={s.site}>
              <SiteTitle>{s.site}</SiteTitle>
              {s.requests.map((g) => (
                <VacancyCard key={g.request_id} g={g} canMark={canMark} />
              ))}
            </SiteBlock>
          ))}
        </KadryStack>
      )}
    </>
  );
}
